import { useState } from "react";
import { braceletMaterialFields, caseMaterialFields, movementFields, stateFields } from "../../../utils/fields";
import ExpandableFilter from "./expandlableFilter";
import LoadingBalls from "../../core/loadingBalls";
import { useWatches } from "../../../context/watchProvider";
import { DocumentData, QueryDocumentSnapshot, WhereFilterOp } from "firebase/firestore";

export default function Filters({ setWatches, loading, setLoading, setSelectedBrand, setQuery, setLast, setTotal }: {
    setWatches: React.Dispatch<React.SetStateAction<{ id: string, [key: string]: any }[] | null>>,
    loading: boolean,
    setSelectedBrand: React.Dispatch<React.SetStateAction<string>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setQuery: React.Dispatch<React.SetStateAction<{ filters: any, price: any, year: any }>>,
    setLast: React.Dispatch<React.SetStateAction<QueryDocumentSnapshot<DocumentData, DocumentData> | undefined>>,
    setTotal: React.Dispatch<React.SetStateAction<number>>,
}) {

    const { searchWatches } = useWatches();

    const [states, setStates] = useState<string[]>([]);
    const [brand, setBrand] = useState("");
    const [yearFrom, setYearFrom] = useState("");
    const [yearTo, setYearTo] = useState("");
    const [priceFrom, setPriceFrom] = useState("");
    const [priceTo, setPriceTo] = useState("");
    const [movement, setMovement] = useState("");
    const [caseMaterial, setCaseMaterial] = useState("");
    const [braceletMaterial, setBraceletMaterial] = useState("");
    const [glass, setGlass] = useState("");

    const handleStates = (value: string) => {
        if (states.includes(value)) {
            setStates(s => s.filter(s => s !== value));
            return null;
        }
        setStates(s => [...s, value]);
    }

    const handleYearFrom = (value: string) => {
        if (value === "") {
            setYearFrom("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        setYearFrom(value);
    }

    const handleYearTo = (value: string) => {
        if (value === "") {
            setYearTo("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        setYearTo(value);
    }

    const handlePriceFrom = (value: string) => {
        if (value === "") {
            setPriceFrom("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        setPriceFrom(value);
    }

    const handlePriceTo = (value: string) => {
        if (value === "") {
            setPriceTo("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        setPriceTo(value);
    }

    const doSearchWatches = async () => {
        setLoading(true);
        const filters: { field: string, operator: WhereFilterOp, value: string | string[] | number }[] = [];
        if (states.length) {
            filters.push({ field: "state", operator: "in", value: states });
        }
        if (brand) {
            filters.push({ field: "searchTerms", operator: "array-contains", value: brand.toLowerCase() });
        }
        if (movement) {
            filters.push({ field: "movement", operator: "==", value: movement });
        }
        if (caseMaterial) {
            filters.push({ field: "caseMaterial", operator: "==", value: caseMaterial });
        }
        if (braceletMaterial) {
            filters.push({ field: "braceletMaterial", operator: "==", value: braceletMaterial });
        }
        if (glass) {
            filters.push({ field: "glass", operator: "==", value: glass });
        }


        const foundWatches = await searchWatches(filters, priceFrom || priceTo ? { from: parseInt(priceFrom), to: parseInt(priceTo) } : undefined, yearFrom || yearTo ? { from: parseInt(yearFrom), to: parseInt(yearTo) } : undefined);
        if (foundWatches?.data) {
            setWatches(foundWatches?.data);
            setTotal(foundWatches?.total);
            setLast(foundWatches?.last);
            setQuery({ filters, price: priceFrom || priceTo ? { from: parseInt(priceFrom), to: parseInt(priceTo) } : undefined, year: yearFrom || yearTo ? { from: parseInt(yearFrom), to: parseInt(yearTo) } : undefined })
        }
        else {
            setWatches([]);
            setTotal(0);
            setLast(undefined);
            setQuery({ filters, price: null, year: null });
        }
        setLoading(false);
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    const redefineFilters = async () => {
        setStates([]);
        setBrand("");
        setYearFrom("");
        setYearTo("");
        setPriceFrom("");
        setPriceTo("");
        setMovement("");
        setCaseMaterial("");
        setBraceletMaterial("");
        setGlass("");
        setSelectedBrand("");
        setLoading(true);
        const foundWatches = await searchWatches([]);
        if (foundWatches?.data) {
            setWatches(foundWatches.data);
            setTotal(foundWatches.total);
            setLast(foundWatches.last);
            setQuery({ filters: [], price: null, year: null });
        }
        setLoading(false);
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    return (
        <div className="w-full md:w-[280px] bg-white rounded-3xl p-6 shadow-xl shadow-primary/5 border border-primary/5 flex flex-col gap-2 h-fit animate-superfade" onKeyDown={(e) => e.key === "Enter" && !loading && doSearchWatches()}>
            <div className="pb-4 mb-2 border-b border-surface">
                <h3 className="text-sm font-black tracking-[0.2em] text-primary uppercase">Filtros</h3>
            </div>

            <ExpandableFilter name="Marca/Modelo">
                <div className="relative group">
                    <input
                        type="text"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-4 py-3 rounded-xl text-sm transition-all outline-none text-primary font-medium"
                        placeholder="Ex: Rolex, Omega..."
                    />
                </div>
            </ExpandableFilter>

            <ExpandableFilter name="Estado">
                <div className="space-y-3 pt-2">
                    {
                        Object.keys(stateFields).map((sf, i) => (
                            <div key={i} className="flex items-center gap-3 group cursor-pointer" onClick={() => handleStates(sf)}>
                                <div className={`w-5 h-5 rounded border transition-all flex items-center justify-center ${states.includes(sf) ? "bg-secondary border-secondary shadow-md shadow-secondary/20" : "bg-moninho border-surface group-hover:border-secondary"}`}>
                                    {states.includes(sf) && (
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.5 6L4.5 8L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                                <label className={`text-sm font-medium transition-colors cursor-pointer ${states.includes(sf) ? "text-primary" : "text-mono group-hover:text-primary"}`}>
                                    {stateFields[sf]}
                                </label>
                            </div>
                        ))
                    }
                </div>
            </ExpandableFilter>

            <ExpandableFilter name="Ano">
                <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="space-y-1">
                        <span className="text-[10px] font-bold text-mono tracking-widest uppercase ml-1">De</span>
                        <input
                            type="text"
                            value={yearFrom}
                            onChange={(e) => handleYearFrom(e.target.value)}
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-3 py-2 rounded-lg text-sm transition-all outline-none text-primary font-bold text-center"
                            placeholder="2010"
                        />
                    </div>
                    <div className="space-y-1">
                        <span className="text-[10px] font-bold text-mono tracking-widest uppercase ml-1">Até</span>
                        <input
                            type="text"
                            value={yearTo}
                            onChange={(e) => handleYearTo(e.target.value)}
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-3 py-2 rounded-lg text-sm transition-all outline-none text-primary font-bold text-center"
                            placeholder="2024"
                        />
                    </div>
                </div>
            </ExpandableFilter>

            <ExpandableFilter name="Preço">
                <div className="space-y-3 mt-2">
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-mono">R$</span>
                        <input
                            type="text"
                            value={priceFrom}
                            onChange={(e) => handlePriceFrom(e.target.value)}
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white pl-10 pr-4 py-3 rounded-xl text-sm transition-all outline-none text-primary font-bold"
                            placeholder="De"
                        />
                    </div>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-mono">R$</span>
                        <input
                            type="text"
                            value={priceTo}
                            onChange={(e) => handlePriceTo(e.target.value)}
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white pl-10 pr-4 py-3 rounded-xl text-sm transition-all outline-none text-primary font-bold"
                            placeholder="Até"
                        />
                    </div>
                </div>
            </ExpandableFilter>

            <ExpandableFilter name="Especificações">
                <div className="space-y-4 pt-2">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-mono tracking-widest uppercase ml-1">Movimento</label>
                        <select
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-3 py-3 rounded-xl text-sm transition-all outline-none text-primary font-medium"
                            value={movement}
                            onChange={e => setMovement(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {Object.keys(movementFields).map((f, i) => (
                                <option key={i} value={f}>{movementFields[f]}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-mono tracking-widest uppercase ml-1">Material da Caixa</label>
                        <select
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-3 py-3 rounded-xl text-sm transition-all outline-none text-primary font-medium"
                            value={caseMaterial}
                            onChange={e => setCaseMaterial(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {Object.keys(caseMaterialFields).map((f, i) => (
                                <option key={i} value={f}>{caseMaterialFields[f]}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-mono tracking-widest uppercase ml-1">Bracelete</label>
                        <select
                            className="w-full bg-moninho border border-transparent focus:border-secondary focus:bg-white px-3 py-3 rounded-xl text-sm transition-all outline-none text-primary font-medium"
                            value={braceletMaterial}
                            onChange={e => setBraceletMaterial(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {Object.keys(braceletMaterialFields).map((f, i) => (
                                <option key={i} value={f}>{braceletMaterialFields[f]}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </ExpandableFilter>

            <div className="flex flex-col gap-3 pt-6 mt-4 border-t border-surface">
                <button
                    disabled={loading}
                    onClick={doSearchWatches}
                    className="w-full h-12 bg-primary text-secondary font-black tracking-widest text-xs rounded-xl hover:bg-primary/95 transition-all shadow-lg shadow-primary/10 active:scale-95 flex items-center justify-center"
                >
                    {loading ? <LoadingBalls color="#C5A059" width={30} /> : "APLICAR FILTROS"}
                </button>
                <button
                    disabled={loading}
                    onClick={redefineFilters}
                    className="w-full h-12 bg-white border border-surface text-mono font-bold tracking-widest text-[10px] rounded-xl hover:border-primary hover:text-primary transition-all active:scale-95"
                >
                    REDEFINIR
                </button>
            </div>
        </div>
    )
}